import { useQueryClient } from '@tanstack/react-query';
import { useMobile } from 'common-nextjs';
import { useRouter } from 'next/router';
import React from 'react';
import { Button } from 'ui';
import { displayPrice, pluralize } from 'utils';
import ErrorMessage from '~/components/Form/ErrorMessage';
import LoadingCapableButton from '~/components/Form/LoadingCapableButton';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import {
  checkOnboarding,
  useListFlowOnboarding,
} from '~/hooks/listFlow/useListFlowOnboarding';
import useListFlowValueGuide from '~/hooks/listFlow/useListFlowValueGuide';
import { useAuthBlockedAction } from '~/hooks/useAuthBlockedAction';
import {
  listFlowPublish,
  listFlowSaveDraft,
  listFlowValidate,
} from '~/store/listFlow/flow';
import { getListFlowInfoState } from '~/store/listFlow/slices/info';
import {
  getListFlowErrors,
  getListFlowFieldlessErrors,
  getListFlowItemId,
  getListFlowStatus,
} from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowSubmitButtons: React.FC<Props> = () => {
  const router = useRouter();
  const mobile = useMobile();
  const status = useAppSelector(getListFlowStatus);
  const dispatch = useAppDispatch();
  const errors = useAppSelector(getListFlowErrors);
  const fieldlessErrors = useAppSelector(getListFlowFieldlessErrors);
  const itemState = useAppSelector(getListFlowInfoState);
  const itemId = useAppSelector(getListFlowItemId);
  const onboarding = useListFlowOnboarding();
  const queryClient = useQueryClient();
  const { data: valueGuide } = useListFlowValueGuide();

  let errorElement: React.ReactNode = null;

  if (fieldlessErrors && fieldlessErrors.length > 0) {
    errorElement = <ErrorMessage errors={fieldlessErrors} />;
  } else if (errors && errors.length > 0) {
    errorElement = (
      <ErrorMessage errors="There's a problem with your item..." />
    );
  }

  function scrollToFirstError() {
    setTimeout(() => {
      const errorElement = document.querySelector('[id$="-error"]');
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 200);
  }

  const handleSaveDraft = useAuthBlockedAction(async () => {
    dispatch(
      listFlowSaveDraft({
        router,
        onError: scrollToFirstError,
        onSuccess: async item => {
          await router.push(`/locker/${item.seller.username}/draft`);
        },
      }),
    );
  });

  const handleUpdateItem = useAuthBlockedAction(async () => {
    dispatch(
      listFlowSaveDraft({
        router,
        onSuccess: item => {
          window.location.assign(item.url);
        },
        onError: scrollToFirstError,
      }),
    );
  });

  const handlePublish = useAuthBlockedAction(async () => {
    const r = await dispatch(
      listFlowSaveDraft({
        router,
        onError: scrollToFirstError,
      }),
    );

    // @ts-ignore
    if ('error' in r && Array.isArray(r.error)) {
      return;
    }

    dispatch(
      listFlowPublish({
        router,
        onSuccess: async item => {
          window.location.assign(item.url + '?published=1');
        },
        onError: scrollToFirstError,
      }),
    );
  });

  const handleContinue = useAuthBlockedAction(async () => {
    if ((await checkOnboarding(queryClient)) === 'finished_onboarding') {
      await dispatch(listFlowSaveDraft({ router }));

      dispatch(
        listFlowPublish({
          router,
          onSuccess: async item => {
            window.location.assign(item.url + '?published=1');
          },
          onError: scrollToFirstError,
        }),
      );
    } else {
      dispatch(
        listFlowValidate({
          router,
          onSuccess: async item => {
            window.location.assign(`/selling/onboarding?item_id=${item.id}`);
          },
          onError: scrollToFirstError,
        }),
      );
    }
  });

  const saveDraftButton = (
    <LoadingCapableButton
      disabled={status !== 'done'}
      loading={status === 'saving'}
      text="Save Draft"
      loadingText="Saving..."
      variant="text"
      onClick={handleSaveDraft}
      fluid
    />
  );

  let buttons: React.ReactNode = null;

  if (!onboarding || onboarding !== 'finished_onboarding') {
    buttons = (
      <>
        {saveDraftButton}

        <Button
          disabled={status !== 'done'}
          color="turf-green"
          onClick={handleContinue}
          fluid
        >
          Continue
        </Button>
      </>
    );
  } else if (itemState.state === 'draft') {
    buttons = (
      <>
        {saveDraftButton}

        <LoadingCapableButton
          disabled={status !== 'done'}
          loading={status === 'publishing'}
          color="turf-green"
          text="Publish"
          loadingText="Publishing..."
          onClick={handlePublish}
          fluid
        />
      </>
    );
  } else if (itemId) {
    buttons = (
      <LoadingCapableButton
        disabled={status !== 'done'}
        loading={status === 'saving'}
        color="turf-green"
        text="Update Item"
        loadingText="Updating..."
        onClick={handleUpdateItem}
        fluid
      />
    );
  }

  let content: React.ReactNode;
  if (mobile) {
    content = (
      <>
        {valueGuide?.value_guide?.median && (
          <div className="mb-4 flex items-center justify-center space-x-1.5">
            <span className="text-sm font-semibold">Typically sells for</span>
            <span className="text-green-500">
              {displayPrice(valueGuide.value_guide.median)}
            </span>
            <span className="text-slate-green-500 text-sm italic">
              + Shipping costs
            </span>
          </div>
        )}

        {errorElement}

        <div className="flex space-x-2">{buttons}</div>
      </>
    );
  } else {
    content = (
      <div>
        {errorElement}

        <div className="container mb-0 grid grid-cols-3">
          {/* Resale value column */}
          <div>
            <div className="mb-1 text-base font-semibold">
              Estimated Resale Value
            </div>
            <div className="text-slate-green-500 text-sm">
              {valueGuide?.paging?.total_count !== 0
                ? `Based on ${valueGuide?.paging?.total_count} ${pluralize(
                    valueGuide?.paging?.total_count,
                    'sale',
                    'sales',
                  )}`
                : '--'}
            </div>
          </div>

          <div>
            <div className="mb-1 text-base font-semibold">
              Typically Sells For
            </div>
            {valueGuide?.value_guide?.median != null ? (
              <div className="flex items-center space-x-1.5">
                <div className="text-xl text-green-500">
                  {displayPrice(valueGuide?.value_guide?.median)}
                </div>
                <div className="text-slate-green-500 text-sm italic">
                  + Shipping costs
                </div>
              </div>
            ) : (
              '--'
            )}
          </div>

          <div className="flex items-center space-x-2">{buttons}</div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="shadow-top fixed bottom-0 left-0 right-0 z-10 border-t bg-white px-4 py-2">
        {content}
      </div>

      <ListFlowSection>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2">
          <div />
          <div className="flex items-center justify-stretch space-x-2">
            {buttons}
          </div>
        </div>
      </ListFlowSection>
    </>
  );
};

export default ListFlowSubmitButtons;
